import { Component, inject } from '@angular/core';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { FNFResponse } from '../../../models/response';
import { RestaurantService } from '../../../services/restaurant.service';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { isFnfLike, isPosManaged } from '../../../utils/pos-provider.util';

@Component({
  selector: 'app-category',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule],
  templateUrl: './category.component.html',
  styleUrl: './category.component.css',
})
export class CategoryComponent {
  ngxSpinnerService = inject(NgxSpinnerService);
  restaurantService = inject(RestaurantService);

  typesAnsCategories: any = []; // Stores types and their associated categories
  /** Types after the search filter; what both views render. */
  visibleTypes: any[] = [];
  activeAccordionIndex: number | null = null; // Tracks the currently expanded accordion index
  displayLoading: boolean = false;
  restaurantPosProvider: string = 'fnf';

  /** Catalogue synced from Square / Clover / Swirepay, so editing is off. */
  get isPosManagedProvider(): boolean {
    return isPosManaged(this.restaurantPosProvider);
  }
  /** 'list' is the expandable table; 'grid' is the card layout. */
  view: 'list' | 'grid' = 'list';
  /** List / Grid lives in the URL (?view). */
  private urlState = injectUrlViewState();
  searchQuery: string = '';
  /** Categories shown on a grid card before the overflow pill. */
  readonly gridCategoryLimit = 4;

  changeView(view: 'list' | 'grid') {
    this.view = view;
    this.urlState.set('view', view, 'list');
  }

  filterTypes() {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      this.visibleTypes = [...this.typesAnsCategories];
      return;
    }
    this.visibleTypes = this.typesAnsCategories.filter((type: any) => {
      const names = (type.categories || []).map((c: any) => c.name).join(' ');
      return `${type.name} ${names}`.toLowerCase().includes(query);
    });
  }

  selectionLabel(type: any): string {
    return type?.is_multi_category ? 'Multi Select' : 'Single Select';
  }

  visibleCategories(type: any): any[] {
    return (type?.categories || []).slice(0, this.gridCategoryLimit);
  }

  overflowCount(type: any): number {
    return Math.max(0, (type?.categories || []).length - this.gridCategoryLimit);
  }

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private toastr: ToastrService,
  ) {
    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
    });
  }

  ngOnInit() {
    this.view = this.urlState.pick<'list' | 'grid'>('view', ['list', 'grid'], 'list');
    // Initialize by fetching types and categories
    this.getRestaurantTypeAndCategories();
  }

  // Fetches restaurant types and categories from the service.
  private getRestaurantTypeAndCategories() {
    this.displayLoading = true;
    this.restaurantService.getRestaurantTypeAndCategories().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.typesAnsCategories = response.data;
          this.typesAnsCategories.sort((a: any, b: any) => a.name.localeCompare(b.name));
          this.filterTypes();
        }
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  // type Optional type to edit; if not provided, adds a new type.
  addNewType(type?: any) {
    if (isPosManaged(this.restaurantPosProvider) && !type) {
      return;
    }
    this.router.navigate(type ? ['/edit-type', type.id] : ['/add-type']);
  }

  // Edits an existing type.
  // Deletes a type after confirmation.
  deleteType(type: any) {
    if (!type.is_default && isFnfLike(this.restaurantPosProvider)) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message:
            'This will permanently delete the ' + type.name + ' and you can’t undo this later.',
          buttonText: { ok: 'Yes, Delete', cancel: 'No, Cancel' },
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteMenuType(type);
        }
      });
    }
  }

  // Deletes a type from the server.
  deleteMenuType(type: any) {
    this.ngxSpinnerService.show();
    this.restaurantService.deleteMenuType(type.id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.getRestaurantTypeAndCategories();
          this.toastr.success(response.data.message, 'Success');
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  // Toggles the expanded state of an accordion.
  toggleAccordion(index: number): void {
    this.activeAccordionIndex = this.activeAccordionIndex === index ? null : index;
  }

  // Opens the dialog to add categories for a specific type.
  addCategory(type: any) {
    if (isPosManaged(this.restaurantPosProvider)) {
      return;
    }
    this.router.navigate(['/add-category', type.id]);
  }

  // Adds categories to a specific type.
  addTypeCategory(type: any, categories: any) {
    this.ngxSpinnerService.show();
    this.restaurantService.addTypeCategory(type.id, categories).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.getRestaurantTypeAndCategories();
          this.toastr.success(response.data.message, 'Success');
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  // Deletes a category from a specific type after confirmation.
  deleteCategory(type: any, category: any) {
    if (!type.is_default && isFnfLike(this.restaurantPosProvider)) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message:
            'This will permanently delete the ' + category.name + ' and you can’t undo this later.',
          buttonText: { ok: 'Yes, Delete', cancel: 'No, Cancel' },
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteTypeCategory(type, category);
        }
      });
    }
  }

  // Deletes a category from a specific type on the server.
  deleteTypeCategory(type: any, category: any) {
    this.ngxSpinnerService.show();
    this.restaurantService.deleteTypeCategory(type.id, category.id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.getRestaurantTypeAndCategories();
          this.toastr.success(response.data.message, 'Success');
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }
}
